<template>
<LayoutSearchFieldVertical
  display-variant="list"
>
  <template #default v-if="code">
    <div class="facet-buttons facet-mobile-grid">
      <button
        class="fb-button"
        v-for="c in codes"
        :class="{
          'is-0': count(c) === 0,
          'is-exclude': exclude,
          'is-selected': isSelected('+' + c),
        }"
        @click="select(c)"
        v-show="showAll || !isDetail(c)"
        :key="c"
      >
        <span class="fb-label">{{ display(c) }}</span>
        <span class="fb-count" :title="num(count(c))" v-if="facet !== null">{{
          facetNum(count(c))
        }}</span>
      </button>
    </div>
  </template>

  <template #header v-else>
    <!-- コード設定が無いときには、ただの入力フィールドを出す -->
    <form class="search-keyword-field" @submit.prevent="addCode">
      <input
        class="jps-keyword-input"
        type="text"
        v-model="value"
        :placeholder="ls(field.placeHolder)"
        @keyup.prevent.esc="value = ''"
      />
      <UiButtonIcon
        icon-size="size-5"
        styled="secondary"
        tag="button"
        type="submit"
        icon="mdi/plus-circle"
      />
    </form>
  </template>

  <template #nav v-if="code">
    <button
      class="jps-text-button"
      v-if="hasDetail()"
      @click="showAll = !showAll"
    >
      {{
        showAll
          ? ls(dapLangGlobal.hide)
          : lss('すべての選択肢を表示', 'Show all')
      }}
    </button>
  </template>
  <template #options v-if="code">
    <BaseToggleSwitch class="is-red" v-model="exclude">{{
      lss('除外条件を指定', 'Exclude')
    }}</BaseToggleSwitch>
  </template>
</LayoutSearchFieldVertical>
</template>

<script lang="ts" setup>
import { useSearchFieldBase } from '~/components/search/search-field/useBaseSearchField'
import type { SearchFieldBaseProps } from '~/components/search/search-field/useBaseSearchField'
import { dapLangGlobal } from '#imports'

const LANGUAGE_DISPLAY_SHOW_COUNT: number = 10
const { ls, lss, lc, num } = useI18n()
const dataStore = useDataStore()

interface Props extends SearchFieldBaseProps {
  displayFunction?: (code: string) => string
}
const props = withDefaults(defineProps<Props>(), {
})

const { facet, excludeDisplay, facetNum } = useSearchFieldBase(props)

function count(code) {
  if (!facet.value) return undefined
  return facet.value.counts[code] || 0
}

const exclude = ref(false)
const showAll = ref(false)

const code = computed(() => {
  if (props.field.codeKey) return dataStore.code(props.field.codeKey)
  return null
})

const codes = computed(() => {
  if (code.value) {
    if (isCodeLanguage.value) {
      // 言語コードの場合は検索後じゃないと表示できないため、検索前は日本語と英語を表示しておく
      if (!facet.value) {
        return ['jpn', 'eng']
      }
      // 並び順をファセットの件数順に変更する
      const filterArray = code.value.values.map(v => v.code).filter(c => c !== 'various').filter(code => count(code))
      filterArray.sort((a, b) => count(b) - count(a))
      const undIndex = filterArray.indexOf('und')
      if (undIndex >= 0) {
        filterArray.splice(undIndex, 1)
        filterArray.push('und')
      }
      return filterArray
    }  else {
      return code.value.values.map(v => v.code).filter(c => c !== 'various')
    }
  }
  return []
})

const select = (code: string) => {
  if (exclude.value) code = '-' + code
  else code = '+' + code
  if (isSelected(code)) {
    props.ss.removeFilter(props.field.key, [code])
  } else {
    props.ss.addFilter(props.field.key, [code])
  }
}

const isSelected = (code: string) => {
  return props.ss.state.filter[props.field.key]?.includes(code)
}

const isDetail = f => {
  if (isCodeLanguage.value) {
    // 言語の場合は指定の件数以上のものを非表示とする
    return codes.value.indexOf(f) >= LANGUAGE_DISPLAY_SHOW_COUNT
  } else {
    return code.value.values.filter(c => c.code === f).pop()?.detail
  }
}

const hasDetail = () => {
  if (isCodeLanguage.value) {
    // 言語の場合は指定の件数を超えてるものを非表示にする
    return codes.value.length > LANGUAGE_DISPLAY_SHOW_COUNT
  } else {
    return code.value?.values.some(c => c.detail)
  }
}

const display = (c: string) => {
  let v = ''
  if (code.value) v = lc(code.value, c)
  else if (props.displayFunction) v = props.displayFunction(c)
  else v = c
  return excludeDisplay(v, exclude.value, count(v))
}

const value: Ref<string> = ref('')

const addCode = () => {
  if (value.value) {
    let word = value.value
    if (!value.value.startsWith('-') || !value.value.startsWith('+')) {
      word = '+' + word
    }
    props.ss.addFilter(props.field.key, [word])
  }
}

const isCodeLanguage = computed<boolean>(() => {
  return props.field?.codeKey === 'jps-language'
})
</script>
